import nacl from 'tweetnacl'
import { decodeBase64, decodeUTF8, encodeBase64, encodeUTF8 } from 'tweetnacl-util'
import { getIdentity, getOrCreateIdentity } from './keyStore'

/**
 * End-to-end message crypto for secret chats.
 *
 * Primitives (tweetnacl):
 *  - message:  nacl.box  = X25519 + XSalsa20-Poly1305 (authenticated).
 *  - media:    nacl.secretbox = XSalsa20-Poly1305 with a random per-file key.
 *
 * Direction-independent: the DH shared secret is DH(mySecret, peerPub) for BOTH
 * sent and received messages, so a message is decrypted the same way regardless
 * of who sent it — the caller only ever needs the chat peer's public key.
 */

export interface EncryptedMediaMeta {
  key: string      // base64 secretbox key (32 bytes)
  nonce: string    // base64 nonce (24 bytes)
  mime: string
  name: string
  size: number
  w?: number
  h?: number
  duration?: number
}

export interface SecretPayload {
  text: string
  media?: EncryptedMediaMeta[]
  answer_to?: number | null
}

export interface Envelope {
  v: 1
  alg: 'box'
  spk: string   // base64 sender device public key (for key-change detection)
  n: string     // base64 nonce
  c: string     // base64 ciphertext
}

/** True if `raw` looks like a secret-chat envelope (defensive, never throws). */
export function isEnvelope(raw: unknown): raw is string {
  if (typeof raw !== 'string' || raw.length < 8 || raw[0] !== '{') return false
  try {
    const o = JSON.parse(raw)
    return o && o.v === 1 && o.alg === 'box' && typeof o.c === 'string' && typeof o.n === 'string'
  } catch {
    return false
  }
}

/** Encrypt a payload for the chat peer. Returns the JSON envelope string to store in `text`. */
export async function encryptEnvelope(payload: SecretPayload, peerPubBase64: string): Promise<string> {
  const id = await getOrCreateIdentity()
  const nonce = nacl.randomBytes(nacl.box.nonceLength)
  const msg = decodeUTF8(JSON.stringify(payload))
  const cipher = nacl.box(msg, nonce, decodeBase64(peerPubBase64), id.secretKey)

  const env: Envelope = {
    v: 1,
    alg: 'box',
    spk: encodeBase64(id.publicKey),
    n: encodeBase64(nonce),
    c: encodeBase64(cipher),
  }
  return JSON.stringify(env)
}

export interface DecryptResult {
  payload: SecretPayload
  senderPublicKey: string   // base64 spk from the envelope
}

/**
 * Decrypt an envelope using the chat peer's public key + this device's secret key.
 * Returns null when the message cannot be decrypted (wrong/rotated keys, corruption).
 */
export async function decryptEnvelope(envStr: string, peerPubBase64: string): Promise<DecryptResult | null> {
  const id = await getIdentity()
  if (!id) return null

  let env: Envelope
  try {
    env = JSON.parse(envStr)
  } catch {
    return null
  }
  if (!env || env.v !== 1 || env.alg !== 'box') return null

  let opened: Uint8Array | null
  try {
    opened = nacl.box.open(decodeBase64(env.c), decodeBase64(env.n), decodeBase64(peerPubBase64), id.secretKey)
  } catch {
    return null
  }
  if (!opened) return null

  try {
    return { payload: JSON.parse(encodeUTF8(opened)) as SecretPayload, senderPublicKey: env.spk }
  } catch {
    return null
  }
}

/** Encrypt raw file bytes with a fresh random key (for E2E media upload). */
export function encryptBytes(bytes: Uint8Array): { cipher: Uint8Array; key: string; nonce: string } {
  const key = nacl.randomBytes(nacl.secretbox.keyLength)
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength)
  const cipher = nacl.secretbox(bytes, nonce, key)
  return { cipher, key: encodeBase64(key), nonce: encodeBase64(nonce) }
}

/** Decrypt file bytes previously encrypted with {@link encryptBytes}. Null on failure. */
export function decryptBytes(cipher: Uint8Array, keyBase64: string, nonceBase64: string): Uint8Array | null {
  try {
    return nacl.secretbox.open(cipher, decodeBase64(nonceBase64), decodeBase64(keyBase64))
  } catch {
    return null
  }
}

/**
 * Signal-style numeric safety number (60 digits, twelve 5-digit groups) derived
 * from both users' public keys. Two devices show the same number iff neither key
 * was tampered with. Users compare it out-of-band to defeat MITM.
 */
export async function safetyNumber(myPubBase64: string, peerPubBase64: string): Promise<string> {
  // Sort so both sides compute the same value regardless of who is "me".
  const [a, b] = [myPubBase64, peerPubBase64].sort()
  const material = new Uint8Array([...decodeBase64(a), ...decodeBase64(b)])
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-384', material)) // 48 bytes → 12 groups
  const groups: string[] = []
  for (let i = 0; i < 12; i++) {
    const off = i * 4
    const n = ((digest[off] << 24) >>> 0) + (digest[off + 1] << 16) + (digest[off + 2] << 8) + digest[off + 3]
    groups.push(String(n % 100000).padStart(5, '0'))
  }
  return groups.join(' ')
}
